import { Controller } from "@hotwired/stimulus"
import { savePlan, debounce, registerFlush } from "plan_storage"
import { preserveScroll } from "controllers/scroll_preserve"
import { scheduleTimeText, scheduleUnitText, scheduleHasContent } from "schedule_fields"
import { renderIcons } from "lucide_icons"
import { vendorGet } from "vendor_library"

// Unmet Needs list — DERIVED directly from the Goal / Barrier resources
// the user tagged "Unmet Need" in the editor. Each such resource becomes
// one card here, labeled by the service they picked from the resource's
// Service dropdown (r.text || r.type; the typed-in name when the service
// is "Other"). Tagging a resource "Unmet Need" in Goals/Barriers is what
// adds it here — there is no separate curated list to maintain.
//
// The only thing the user edits here is an optional Requested Frequency
// per need, stored in plan.unmetFreq keyed by the resource id so it
// round-trips with the plan without colliding with the goal-resources
// editor's own writes to that resource.

const STORAGE_KEY = "pcp_plan_data"

export default class extends Controller {
  static targets = ["list", "empty"]

  connect() {
    this.plan = this._load()
    this._render()
    this._onChange = (e) => {
      if (this._suppressNextChange) { this._suppressNextChange = false; return }
      const p = e.detail?.path || ""
      // Re-derive whenever goals / barriers (where the unmet resources
      // live) or our own frequency store changes. Nested leaf edits count
      // — a service/tag change on a resource must re-flow here.
      if (p === "goals" || p.startsWith("goals.") ||
          p === "barriers" || p.startsWith("barriers.") ||
          p === "unmetNeeds" || p.startsWith("unmetNeeds.") ||
          p === "*") {
        this.plan = e.detail.plan || this._load()
        this._render()
      }
    }
    document.addEventListener("plan:change", this._onChange)
    this._unregFlush = registerFlush(() => this._debSave?.flush())
  }

  disconnect() {
    this._debSave?.flush()
    this._unregFlush?.()
    document.removeEventListener("plan:change", this._onChange)
  }

  // ---- requested frequency (stored in plan.unmetFreq, keyed by res id) ----
  updateFrequency(event) {
    const id = event.currentTarget.dataset.unId
    if (!this.plan.unmetFreq) this.plan.unmetFreq = {}
    this.plan.unmetFreq[id] = event.currentTarget.value
    // Debounce the persist + broadcast; the model is updated above so no
    // keystroke is lost, and disconnect() flushes any pending write.
    ;(this._debSave ||= debounce(() => this._save({ rerender: false }), 200, { dirty: true }))()
  }

  // ---- Vendor picker integration ----
  // Handle vendor-picker:change events on the vendor cell. The picker fires
  // this when the user selects or adds a vendor from the library. We store
  // vendorIds[] on the resource alongside the legacy vendors array.
  handleVendorChange(event) {
    const rid = event.currentTarget.dataset.unId
    const r = this._findResource(rid)
    if (!r) return
    const { id: vendorId, vendor } = event.detail || {}
    if (!Array.isArray(r.vendorIds)) r.vendorIds = []
    if (vendorId && !r.vendorIds.includes(vendorId)) {
      r.vendorIds.push(vendorId)
      // Keep legacy vendors[] in sync so old readers see something
      if (!Array.isArray(r.vendors)) r.vendors = []
      r.vendors.push({ id: Date.now() + Math.random(), name: vendor?.name || "", vendorId })
    }
    this._saveResource()
    this._render()
  }

  // Remove a linked vendor from the resource
  removeVendor(event) {
    event.preventDefault()
    const rid = event.currentTarget.dataset.unId
    const vid = event.currentTarget.dataset.vendorId
    const r = this._findResource(rid)
    if (!r) return
    if (Array.isArray(r.vendorIds)) {
      r.vendorIds = r.vendorIds.filter(id => id !== vid)
    }
    if (Array.isArray(r.vendors)) {
      r.vendors = r.vendors.filter(v => v.vendorId !== vid)
    }
    this._saveResource()
    this._render()
  }

  // Find a resource by id across goals and barriers
  _findResource(rid) {
    for (const g of (this.plan.goals || [])) {
      const r = (g.resources || []).find(x => String(x.id) === String(rid))
      if (r) return r
    }
    for (const b of (this.plan.barriers || [])) {
      const r = (b.resources || []).find(x => String(x.id) === String(rid))
      if (r) return r
    }
    return null
  }

  // Save the plan after modifying a resource (vendor link change)
  _saveResource() {
    savePlan(this.plan)
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "goals", plan: this.plan, nested: true }
    }))
  }

  // Clicking a "Goal N" / "Barrier N" badge switches to that section (via
  // its sidebar nav link) and scrolls the Nth item into view with a brief
  // highlight.
  jumpToRef(event) {
    event.preventDefault?.()
    const m = /^(Goal|Barrier)\s+(\d+)$/.exec(event.currentTarget.dataset.unRef || "")
    if (!m) return
    const key = m[1] === "Barrier" ? "barriers" : "goals"
    const idx = parseInt(m[2], 10) - 1
    document.querySelector(`.en-sidebar-link[data-section-key="${key}"]`)?.click()
    setTimeout(() => {
      const rows = document.querySelectorAll(
        `[data-controller~="dynamic-list"][data-dynamic-list-path-value="${key}"] [data-row]`
      )
      const target = rows[idx]
      if (!target) return
      target.scrollIntoView({ behavior: "smooth", block: "center" })
      const prev = target.style.boxShadow
      target.style.transition = "box-shadow .3s ease"
      target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.55)"
      setTimeout(() => { target.style.boxShadow = prev }, 1600)
    }, 140)
  }

  // ---- data ----
  // Every Goal / Barrier resource tagged "unmet", in document order.
  _unmetResources() {
    const out = []
    ;(this.plan.goals || []).forEach((g, gi) => {
      ;(g.resources || []).forEach(r => {
        if (r && r.tag === "unmet") out.push({ r, ref: `Goal ${gi + 1}` })
      })
    })
    ;(this.plan.barriers || []).forEach((b, bi) => {
      ;(b.resources || []).forEach(r => {
        if (r && r.tag === "unmet") out.push({ r, ref: `Barrier ${bi + 1}` })
      })
    })
    return out
  }

  // ---- render ----
  _render() {
    if (!this.hasListTarget) return
    preserveScroll(this.element, () => {
      const rows = this._unmetResources()
      if (this.hasEmptyTarget) this.emptyTarget.hidden = rows.length > 0
      this.listTarget.innerHTML = rows.length ? `
        <table class="un-table">
          <thead>
            <tr>
              <th class="un-th-need">Unmet Need</th>
              <th class="un-th-src">Goal / Barrier</th>
              <th class="un-th-freq">Requested Frequency</th>
              <th class="un-th-vendor">Vendor Name</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(row => this._renderRow(row)).join("")}
          </tbody>
        </table>` : ""
      renderIcons(this.element)
    })
  }

  _renderRow({ r, ref }) {
    const id = this._esc(String(r.id))
    // Unmet Need = the resource's Service-dropdown selection. "Other"
    // shows the typed-in name (r.comment) instead of the literal "Other".
    const svc = (r.text || r.type || "").trim()
    const isOther = svc === "Other"
    const service = isOther ? (r.comment || "Other") : (svc || "Untitled service")
    // Frequency comes from the resource's own schedule (its Frequency
    // rows), rendered as chips; fall back to a free-text "requested
    // frequency" input only when the resource has no schedule yet.
    const schedChips = this._schedChips(r.schedule)
    const freq = (this.plan.unmetFreq && this.plan.unmetFreq[String(r.id)]) || ""
    const freqCell = schedChips.length
      ? schedChips.join(" ")
      : `<input type="text" class="un-freq-input"
                data-action="input->unmet-needs-list#updateFrequency"
                data-un-id="${id}"
                value="${this._esc(freq)}"
                placeholder="e.g. 2 hrs/wk">`
    const vendorCell = this._vendorPickerCell(r, id)
    return `
      <tr class="un-tr">
        <td class="un-td-need"><span class="un-need-name">${this._esc(service)}</span></td>
        <td class="un-td-src">${this._refBadge(ref)}</td>
        <td class="un-td-freq">${freqCell}</td>
        <td class="un-td-vendor">${vendorCell}</td>
      </tr>`
  }

  // Render the vendor picker cell for a resource. Shows:
  // - Linked vendors as chips with remove button
  // - Legacy vendors (no vendorId) as plain text
  // - A vendor-picker search input to add more
  _vendorPickerCell(r, id) {
    const vendorIds = Array.isArray(r.vendorIds) ? r.vendorIds : []
    const legacyVendors = (Array.isArray(r.vendors) ? r.vendors : [])
      .filter(v => v && v.name && !v.vendorId)

    // Render linked vendors as chips
    const linkedChips = vendorIds.map(vid => {
      const vendor = vendorGet(vid)
      const name = vendor?.name || "(unknown)"
      return `<span class="un-vendor-chip">
        <span class="un-vendor-name">${this._esc(name)}</span>
        <button type="button" class="un-vendor-remove"
                data-action="click->unmet-needs-list#removeVendor"
                data-un-id="${id}" data-vendor-id="${this._esc(vid)}"
                title="Remove vendor">&times;</button>
      </span>`
    }).join("")

    // Render legacy vendors (unlinked) as plain text
    const legacyText = legacyVendors.map(v => this._esc(v.name)).join(", ")

    // The vendor-picker input for adding new vendors
    const picker = `<div class="un-vendor-picker"
         data-controller="vendor-picker"
         data-vendor-picker-value-value=""
         data-vendor-picker-name-value=""
         data-vendor-picker-placeholder-value="Add vendor..."
         data-un-id="${id}"
         data-action="vendor-picker:change->unmet-needs-list#handleVendorChange">
    </div>`

    const hasContent = vendorIds.length > 0 || legacyVendors.length > 0
    return `<div class="un-vendor-cell">
      ${linkedChips}
      ${legacyText ? `<span class="un-vendor-legacy">${legacyText}</span>` : ""}
      ${picker}
    </div>`
  }

  _refBadge(ref) {
    const isBarrier = /^Barrier/.test(ref)
    const cls = isBarrier ? "cs-ref-barrier" : "cs-ref-goal"
    return `<span class="cs-ref-pill ${cls}"
                  role="button" tabindex="0"
                  data-action="click->unmet-needs-list#jumpToRef"
                  data-un-ref="${this._esc(ref)}"
                  title="Jump to ${this._esc(ref)}">${this._esc(ref)}</span>`
  }

  // Each of a resource's schedule rows ("Add Frequency and Schedule")
  // as the SAME frequency chip the Review/PDF preview uses — a pill with
  // the dayType on the left, an arrow, then the time/unit on a blue pill
  // (e.g. "Weekly → 2 sessions 1 hrs/wk"). Mirrors print_preview's
  // pp-work-sched chip so the editor list and the preview read identically.
  // Uses the CANONICAL schedule formatter (schedule_fields.js) so unmet needs
  // reads identically to the goal cards, current services, and the PDF/preview
  // — including the Assessment-needed / Weekly-Schedule-needed tags.
  _schedChips(schedule) {
    return (Array.isArray(schedule) ? schedule : [])
      .filter(scheduleHasContent)
      .map(s => {
        const dayType = (s.dayType || "").trim()
        const timeTxt = `${scheduleTimeText(s)}${scheduleUnitText(s)}`
        return `
          <span class="un-sched">
            <span class="un-sched-d">${this._esc(dayType)}</span>
            <span class="un-sched-arr">&rarr;</span>
            <span class="un-sched-t">${this._esc(timeTxt)}</span>
          </span>`
      })
  }

  // ---- persistence ----
  _load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") }
    catch (_) { return {} }
  }
  // Only plan.unmetFreq changes here — dispatch on the "unmetNeeds" path
  // so goal-resources (which owns the resource) isn't disturbed. nested
  // when we don't want a re-render (frequency keystrokes keep focus).
  _save({ rerender = true } = {}) {
    savePlan(this.plan)
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "unmetNeeds", plan: this.plan, nested: rerender === false }
    }))
  }
  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
